import React, { useEffect, useState } from "react";
import Datatable from "../../datatable/Datatable";
import Navbar from "../navbar/navbar";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  checkUsername,
  checkEmail,
  createUser,
  deleteUser,
  getRoleDetails,
  getUserById,
  getUserMasterList,
  updateUser,
} from "../../services/admin.service";
import { getDevoteeMasterData } from "../../services/receipt.service";
import { FaEdit, FaTrash } from "react-icons/fa";
import AlertConfirmation from "../../common/AlertConfirmation.component";
import "../admin/adminModule.css";

const UserManagerListComponent = () => {
  const [userMasterList, setUserMasterList] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    fetchRoleData();
    fetchUserList();
  }, []);

  const fetchUserList = async () => {
    try {
      const list = await getUserMasterList();
      setUserMasterList(list || []);
    } catch (error) {
      console.error("Failed to fetch user list", error);
    }
  };

  const fetchRoleData = async () => {
    try {
      const list = await getRoleDetails();
      setRoleList(list || []);
    } catch (error) {
      console.error("Failed to fetch role details", error);
    }
  };

  const initialValues = {
    username: editData?.username || "",
    name: editData?.name || "",
    phone: editData?.phone || (editData?.username && /^[0-9]{10}$/.test(editData?.username) ? editData.username : ""),
    gender: editData?.gender || "Not Specified",
    email: editData?.email || "",
    roleId: editData?.roleId || "",
  };

  const columns = [
    { name: "SN", selector: (row) => row.sn, sortable: true, align: "text-center", width: "60px" },
    { name: "User Name", selector: (row) => row.username, sortable: true, align: "text-start", width: "150px" },
    { name: "Name", selector: (row) => row.name, sortable: true, align: "text-start", width: "180px" },
    { name: "Phone", selector: (row) => row.phone, sortable: true, align: "text-start", width: "130px" },
    { name: "Gender", selector: (row) => row.gender, sortable: true, align: "text-center", width: "120px" },
    { name: "Email", selector: (row) => row.email, sortable: true, align: "text-start", width: "220px" },
    { name: "Role", selector: (row) => row.roleName, sortable: true, align: "text-start", width: "130px" },
    { name: "Action", selector: (row) => row.action, sortable: false, align: "text-center", width: "110px" },
  ];

  const mappedData = () => {
    return userMasterList.map((item, index) => ({
      sn: index + 1,
      username: item.username || "-",
      name: item.name || "-",
      phone: item.phone || (item.username && /^[0-9]{10}$/.test(item.username) ? item.username : "-"),
      gender: item.gender || "-",
      email: item.email || "-",
      roleName: item.roleName || "-",
      action: (
        <>
          <button
            className="btn btn-sm btn-warning me-2"
            onClick={() => handleEditUser(item)}
            title="Edit User"
          >
            <FaEdit />
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDelete(item)}
            title="Delete User"
          >
            <FaTrash />
          </button>
        </>
      ),
    }));
  };

  const handleAddUser = () => {
    setEditMode(false);
    setEditData(null);
    setOpenModal(true);
  };

  const handleEditUser = async (item) => {
    setEditMode(true);
    const response = await getUserById(item.loginId);
    if (response) {
      setEditData(response);
      setOpenModal(true);
    } else {
      Swal.fire("Error", "Failed to fetch user details", "error");
    }
  };

  const handleDelete = async (item) => {
    if (!item.loginId) {
      Swal.fire("Error", "Failed to delete user", "error");
      return;
    }
    const confirm = await AlertConfirmation({ title: "Are you sure to delete!", message: "" });
    if (!confirm) return;
    const result = await deleteUser(item.loginId);
    if (result) {
      Swal.fire("Deleted", "User deleted successfully", "success");
      fetchUserList();
    } else {
      Swal.fire("Error", "Failed to delete user", "error");
    }
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .trim()
      .required("Username is required")
      .min(3, "Minimum 3 characters"),
    name: Yup.string().trim().required("Name is required"),
    phone: Yup.string()
      .trim()
      .matches(/^[6-9][0-9]{9}$/, "Enter a valid 10-digit mobile number")
      .required("Phone number is required"),
    gender: Yup.string().required("Gender is required"),
    email: Yup.string()
      .trim()
      .email("Enter a valid email address")
      .required("Email is required"),
    roleId: Yup.string().required("Role is required"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        username: values.username.trim(),
        name: values.name.trim(),
        phone: values.phone.trim(),
        gender: values.gender,
        email: values.email.trim().toLowerCase(),
        roleId: values.roleId,
        ...(editData && { loginId: editData.loginId }),
      };

      if (editData) {
        // EDIT
        const confirm = await AlertConfirmation({ title: "Are you sure to update!", message: "" });
        if (!confirm) return;
        const result = await updateUser(payload);
        if (result) {
          Swal.fire("Updated", "User updated successfully", "success");
          fetchUserList();
          resetForm();
          setOpenModal(false);
          setEditMode(false);
          setEditData(null);
        } else {
          Swal.fire("Error", "Failed to update user", "error");
        }
      } else {
        // ADD
        const confirm = await AlertConfirmation({ title: "Are you sure to add!", message: "" });
        if (!confirm) return;
        const result = await createUser(payload);
        if (result) {
          Swal.fire("Added", "User created successfully! Welcome email sent to registered email.", "success");
          fetchUserList();
          resetForm();
          setOpenModal(false);
          setEditMode(false);
          setEditData(null);
        } else {
          Swal.fire("Error", "Failed to create user", "error");
        }
      }
    } catch (error) {
      console.error("Error saving user:", error);
      let msg = "Failed to save user";
      if (error.response?.data?.title) {
        msg = error.response.data.title;
      } else if (error.response?.data?.message) {
        msg = error.response.data.message;
      } else if (error.response?.headers?.["x-tms-error"]) {
        msg = decodeURIComponent(error.response.headers["x-tms-error"]);
      }
      Swal.fire("Error", msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <Navbar />
      <div className="card-body">
        <div className="row">
          <div className="col-md-12 text-center">
            <h3 className="admin-custom-heading">User Manager List</h3>
          </div>
        </div>
      </div>
      <div id="card-body customized-card" className="p-2">
        <Datatable columns={columns} data={mappedData()} />
      </div>
      <div className="text-center mb-3">
        <button type="button" className="add" onClick={handleAddUser}>
          Add New
        </button>
      </div>

      {openModal && (
        <div className="modal show d-block cs-modal-bg" tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-warning">
                <h5 className="modal-title">{editMode ? "Edit User" : "Add New User"}</h5>
                <button type="button" className="btn-close" onClick={() => setOpenModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ setFieldError, setFieldValue }) => (
                    <Form>
                      {/* FIRST ROW: User Name, Name, Phone */}
                      <div className="row g-3 mb-3">
                        {/* User Name */}
                        <div className="col-md-4">
                          <label className="form-label fw-bold">User Name</label>
                          <Field name="username">
                            {({ field }) => (
                              <input
                                type="text"
                                {...field}
                                className="form-control"
                                placeholder="Enter User Name"
                                maxLength={50}
                                onBlur={async (e) => {
                                  field.onBlur(e);
                                  const value = e.target.value?.trim();
                                  if (value && value.length >= 3 && value !== editData?.username) {
                                    try {
                                      const res = await checkUsername(value);
                                      if (!res.available) {
                                        setFieldError("username", "Username already exists");
                                      }
                                    } catch (err) {
                                      console.error("Error checking username", err);
                                    }
                                  }
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="username" component="div" className="text-danger small" />
                        </div>

                        {/* Name */}
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Name</label>
                          <Field
                            type="text"
                            name="name"
                            className="form-control"
                            placeholder="Enter Full Name"
                          />
                          <ErrorMessage name="name" component="div" className="text-danger small" />
                        </div>

                        {/* Phone */}
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Phone (Mobile No)</label>
                          <Field name="phone">
                            {({ field, form }) => (
                              <input
                                type="text"
                                {...field}
                                className="form-control"
                                placeholder="Enter 10-digit Mobile No"
                                maxLength={10}
                                onBlur={async (e) => {
                                  field.onBlur(e);
                                  const value = e.target.value?.trim();
                                  if (!value) return;

                                  // If username is empty in Add Mode, auto-fill username with phone
                                  if (!editMode && (!form.values.username || form.values.username.trim() === "")) {
                                    setFieldValue("username", value);
                                  }

                                  // If valid 10-digit mobile in Add Mode, auto-fill name from devotee database
                                  if (!editMode && /^[6-9][0-9]{9}$/.test(value)) {
                                    try {
                                      const devotee = await getDevoteeMasterData(value);
                                      if (devotee && devotee.nameOfDevotee) {
                                        if (!form.values.name || form.values.name.trim() === "") {
                                          setFieldValue("name", devotee.nameOfDevotee);
                                        }
                                      }
                                    } catch (err) {
                                      // Devotee not found is fine, admin types name
                                    }
                                  }
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="phone" component="div" className="text-danger small" />
                        </div>
                      </div>

                      {/* SECOND ROW: Email, Gender, Role */}
                      <div className="row g-3">
                        {/* Email */}
                        <div className="col-md-5">
                          <label className="form-label fw-bold">Email</label>
                          <Field name="email">
                            {({ field }) => (
                              <input
                                type="email"
                                {...field}
                                className="form-control"
                                placeholder="Enter Email Address"
                                onBlur={async (e) => {
                                  field.onBlur(e);
                                  const value = e.target.value?.trim();
                                  if (value && value !== editData?.email) {
                                    try {
                                      const res = await checkEmail(value);
                                      if (!res.available) {
                                        setFieldError("email", "Email already registered");
                                      }
                                    } catch (err) {
                                      console.error("Error checking email", err);
                                    }
                                  }
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="email" component="div" className="text-danger small" />
                        </div>

                        {/* Gender */}
                        <div className="col-md-3">
                          <label className="form-label fw-bold">Gender</label>
                          <Field as="select" name="gender" className="form-select">
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Not Specified">Not Specified</option>
                          </Field>
                          <ErrorMessage name="gender" component="div" className="text-danger small" />
                        </div>

                        {/* Role */}
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Role</label>
                          <Field as="select" name="roleId" className="form-select">
                            <option value="">Select Role</option>
                            {roleList &&
                              roleList.map((role, i) => (
                                <option key={i} value={role.roleId}>
                                  {role.roleName}
                                </option>
                              ))}
                          </Field>
                          <ErrorMessage name="roleId" component="div" className="text-danger small" />
                        </div>
                      </div>

                      <div className="text-center mt-4">
                        <button type="submit" className="submit me-2">
                          {editMode ? "Update" : "Submit"}
                        </button>
                        <button type="button" className="back" onClick={() => setOpenModal(false)}>
                          Cancel
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagerListComponent;
